"use client";

import { getTextDirection, LayoutDirection } from "@/utils/locale";
import { useLocale } from "next-intl";
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from "react";

type LayoutContextType = {
  isLoading: boolean;
  isPageMode: boolean;
  title: string;
  subtitle: string;
  showLogo: boolean;
  showNavigation: boolean;
  showBackButton: boolean;
  backButtonUrl: string | undefined;
  backButtonText: string | undefined;
  layoutDir: LayoutDirection;
  setIsLoading: (isLoading: boolean) => void;
  setIsPageMode: (isPageMode: boolean) => void;
  setTitle: (title: string) => void;
  setSubTitle: (subTitle: string) => void;
  setShowLogo: (showLogo: boolean) => void;
  setShowNavigation: (showNavigation: boolean) => void;
  setShowBackButton: (showBackButton: boolean) => void;
  setBackButtonText: (text: string) => void;
  setBackButtonUrl: (url: string) => void;
  setLayoutDir: Dispatch<SetStateAction<LayoutDirection>>;
  reset: () => void;
};

const defaultState = {
  isLoading: false,
  isPageMode: false,
  title: "",
  subtitle: "",
  showLogo: false,
  showNavigation: false,
  showBackButton: false,
  backButtonUrl: "",
  backButtonText: "",
  layoutDir: "ltr" as LayoutDirection,
  setIsLoading: () => {},
  setIsPageMode: () => {},
  setTitle: () => {},
  setSubTitle: () => {},
  setShowLogo: () => {},
  setShowNavigation: () => {},
  setShowBackButton: () => {},
  setBackButtonText: () => {},
  setBackButtonUrl: () => {},
  setLayoutDir: () => {},
  reset: () => {},
};

const LayoutContext = createContext<LayoutContextType | undefined>(
  defaultState,
);

export const LayoutProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(defaultState.isLoading);
  const [isPageMode, setIsPageMode] = useState(defaultState.isPageMode);
  const [title, setTitle] = useState(defaultState.title);
  const [showLogo, setShowLogo] = useState(defaultState.showLogo);
  const [subtitle, setSubTitle] = useState(defaultState.subtitle);
  const [showNavigation, setShowNavigation] = useState(
    defaultState.showNavigation,
  );
  const [backButtonText, setBackButtonText] = useState("");
  const [showBackButton, setShowBackButton] = useState(
    defaultState.showBackButton,
  );
  const [backButtonUrl, setBackButtonUrl] = useState<string | undefined>(
    defaultState.backButtonUrl,
  );
  const locale = useLocale();
  const layoutDirs: LayoutDirection = getTextDirection(locale);
  const [layoutDir, setLayoutDir] = useState<LayoutDirection>(layoutDirs);

  const reset = () => {
    setIsLoading(false);
    setIsPageMode(false);
    setTitle("");
    setShowLogo(false);
    setShowNavigation(false);
    setShowBackButton(false);
    setBackButtonText("");
    setBackButtonUrl("");
  };

  return (
    <LayoutContext.Provider
      value={{
        isLoading,
        isPageMode,
        title,
        showLogo,
        subtitle,
        showNavigation,
        showBackButton,
        backButtonText,
        backButtonUrl,
        layoutDir,
        setIsLoading,
        setIsPageMode,
        setTitle,
        setShowLogo,
        setSubTitle,
        setShowNavigation,
        setShowBackButton,
        setBackButtonUrl,
        setBackButtonText,
        setLayoutDir,
        reset,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }

  return context;
};
